import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CalendarEvent, CalendarMonthViewDay, CalendarView, collapseAnimation, } from 'angular-calendar';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { Subject } from 'rxjs/Subject';
import { CompleteDayEditor } from '../../../models/complete-day.models';
import { ImplementerService } from '../../../services/implementer/implementer.service';
import { ErrorType, ValidationError } from '../../../models/projects/validation.models';
import { NavController } from 'ionic-angular';
import { DailyLogPage } from '../../daily-log/daily-log/daily-log';
import { ChangeHistoryPage } from '../../change-history/change-history/change-history';
import { AppConfig } from '../../../app/app.config';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import { DateService } from '../../../services/date/date.service';

type CalendarPeriod = 'day' | 'week' | 'month';

function addPeriod(period: CalendarPeriod, date: Date, amount: number): Date {
  return {
    day: addDays,
    week: addWeeks,
    month: addMonths,
  }[period](date, amount);
}

function subPeriod(period: CalendarPeriod, date: Date, amount: number): Date {
  return {
    day: subDays,
    week: subWeeks,
    month: subMonths,
  }[period](date, amount);
}

function startOfPeriod(period: CalendarPeriod, date: Date): Date {
  return {
    day: startOfDay,
    week: startOfWeek,
    month: startOfMonth,
  }[period](date);
}

function endOfPeriod(period: CalendarPeriod, date: Date): Date {
  return {
    day: endOfDay,
    week: endOfWeek,
    month: endOfMonth,
  }[period](date);
}

@Component({
  selector: 'implementer-navigator-calendar-component',
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: 'implementer-navigator-calendar-component.html',
  animations: [collapseAnimation]
})
export class ImplementerNavigatorCalendarComponent implements OnInit {
  @Input() dailyLogDate: PtLuxonDate;
  @Input() minDateTime: DateTime;
  @Input() maxDateTime: DateTime;
  minDate: Date;
  maxDate: Date;
  @Input() completeDayEditors: CompleteDayEditor[];
  @Input() snapshotCreationTime: PtLuxonDate;
  @Input() snapshotSummaryId: number;

  @Output() calendarLoaded: EventEmitter<boolean> = new EventEmitter();

  config = AppConfig.settings;

  colours: any = {
    red: {
      primary: '#ad2121',
      secondary: '#FAE3E3',
    },
    blue: {
      primary: '#1e90ff',
      secondary: '#D1E8FF',
    },
    yellow: {
      primary: '#e3bc08',
      secondary: '#FDF1BA',
    },
  };

  view: CalendarView | CalendarPeriod = CalendarView.Month;
  viewDate: Date = addMonths(new Date(), 1);
  events: CalendarEvent[] = [];
  prevBtnDisabled: boolean = false;
  nextBtnDisabled: boolean = false;
  refresh: Subject<any> = new Subject();
  activeDayIsOpen: boolean;

  constructor(
    private navCtrl: NavController,
    private implementerService: ImplementerService,
    private timeZoneService: TimeZoneService,
    private dateService: DateService
  ) {
    this.dateOrViewChanged();
  }

  async ngOnInit() {
    if (this.minDateTime) {
      this.minDate = this.dateService.convertDateTimeToLocalJSDate(this.minDateTime);
    }
    if (this.maxDateTime) {
      this.maxDate = this.dateService.convertDateTimeToLocalJSDate(this.maxDateTime);
    }

    this.completeDayEditors = await this.implementerService.getImplementerCompleteDayEditors(this.config.isVesselEnvironment);

    for (let i = 0; i < this.completeDayEditors.length; i++) {
      if (this.completeDayEditors[i].errorsJson) {
        const errors = JSON.parse(this.completeDayEditors[i].errorsJson) as ValidationError[];

        for (const error of errors) {
          if (error.type === ErrorType.Warning) {
            this.events.push({
              start: this.dateService.convertDateTimeToLocalJSDate(this.completeDayEditors[i].ptLuxonDate.dateTime),
              title: error.message,
              color: this.colours.yellow,
              cssClass: 'calendar-custom-event',
              actions: null,
            });
          }

          if (error.type === ErrorType.Error) {
            this.events.push({
              start: this.dateService.convertDateTimeToLocalJSDate(this.completeDayEditors[i].ptLuxonDate.dateTime),
              title: error.message,
              color: this.colours.red,
              actions: null,
            });
          }
        }
      }
    }

    if (this.dailyLogDate && this.dailyLogDate.dateTime) {
      this.changeDate(this.dateService.convertDateTimeToLocalJSDate(this.dailyLogDate.dateTime));
    }

    this.calendarLoaded.emit(true);
  }

  increment(): void {
    this.changeDate(addPeriod(this.view, this.viewDate, 1));
  }

  decrement(): void {
    this.changeDate(subPeriod(this.view, this.viewDate, 1));
  }

  today(): void {
    this.changeDate(this.dateService.convertDateTimeToLocalJSDate(this.dailyLogDate.dateTime));
  }

  dateIsValid(date: Date): boolean {
    return date >= this.minDate && date <= this.maxDate;
  }

  changeDate(date: Date): void {
    this.viewDate = date;
    this.dateOrViewChanged();
  }

  async openLogAtViewDate() {
    let dateTime = await this.dateService.convertLocalJSDateToDateTime(this.viewDate);
    await this.navCtrl.setRoot(DailyLogPage, {inputDate: dateTime});
  }

  async viewChangeHistory() {
    const ptLuxonDate = new PtLuxonDate(await this.dateService.convertLocalJSDateToDateTime(this.viewDate));

    await this.navCtrl.push(ChangeHistoryPage, {
      startDailyLogDate: ptLuxonDate,
      creationTimeFilterDate: this.snapshotCreationTime,
      snapshotSummaryId: this.snapshotSummaryId
    });
  }

  dateOrViewChanged(): void {
    this.prevBtnDisabled = !this.dateIsValid(
      endOfPeriod(this.view, subPeriod(this.view, this.viewDate, 1))
    );
    this.nextBtnDisabled = !this.dateIsValid(
      startOfPeriod(this.view, addPeriod(this.view, this.viewDate, 1))
    );
    if (this.viewDate < this.minDate) {
      this.changeDate(this.minDate);
    } else if (this.viewDate > this.maxDate) {
      this.changeDate(this.maxDate);
    }
  }

  dayClicked({date, events}: { date: Date; events: CalendarEvent<{ error: ValidationError }>[]; }): void {
    if (isSameMonth(date, this.viewDate)) {
      if ((isSameDay(this.viewDate, date) && this.activeDayIsOpen === true)) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
        this.viewDate = date;
      }
    }
  }

  beforeMonthViewRender({body}: { body: CalendarMonthViewDay[] }): void {
    body.forEach((day) => {
      if (!this.dateIsValid(day.date)) {
        day.cssClass = 'cal-disabled';
      }

      const localCompleteDayEditors = this.completeDayEditors.map(e => {
        e.ptLuxonDate.dateTime = e.ptLuxonDate.dateTime.setZone('utc', {keepLocalTime: true});
        e.localDate = this.dateService.convertDateTimeToLocalJSDate(e.ptLuxonDate.dateTime)
        return e;
      })

      const completeDayEditor = localCompleteDayEditors.find(e => e.localDate.getTime() === day.date.getTime());

      if (completeDayEditor) {
        if (completeDayEditor.isComplete) {
          day.cssClass = 'green-highlight';
        } else if (completeDayEditor.errorsJson && completeDayEditor.errorCount > 0) {
          day.cssClass = 'red-highlight';
        } else {
          day.cssClass = 'amber-highlight';
        }
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/pages/implementer/implementer-navigator-calendar-component/implementer-navigator-calendar-component.ts